import { useFeatureFlagContext } from '@fff-web/feature-flags';
import { Info } from '@fff-web/fff-ui-shared/Icon';
import Tooltip from '@fff-web/fff-ui-shared/Tooltip';
import React from 'react';
import { PlanCode } from '../../../interfaces';
import { ShippingCountry } from '../../services/tax';
import formatCurrency from '../../utils/formatCurrency';
import CheckOutDisclaimer from './CheckOutDisclaimer';
import OrderSummary from './OrderSummary';
import styles from './OrderSummaryContainer.module.css';

const TAX_NOTE = 'Please note that you will be charged sales tax, if applicable.';
const LOADING_TAX = 'Calculated at next step';

interface Address {
  shipCountry: ShippingCountry;
  shipZipCode: string;
  shipState: string;
}

interface ProductData {
  choices: any[];
  sku: string;
  hasRefill: boolean;
  hasIntro: boolean;
  subtotal: number;
}

export interface OrderSummaryContainerProps {
  cartId: number | undefined;
  address: Address;
  planCode: PlanCode;
  productData: ProductData;
  acceptedTerms: boolean;
  setAcceptedTerms: (value: boolean) => void;
  taxes: string | number;
  previewLoading: boolean;
  priceInGBP: number;
  shipping: number;
}

const OrderSummaryContainer = (props: OrderSummaryContainerProps) => {
  const {
    address,
    productData,
    acceptedTerms,
    setAcceptedTerms,
    taxes,
    previewLoading,
    priceInGBP,
    shipping,
  } = props;
  const { shipCountry, shipZipCode }: Address = address;
  const { choices, hasRefill, hasIntro } = productData;
  const { disclaimerPriceIncrease: disclaimerPriceIncreaseFF } = useFeatureFlagContext();

  const getTaxText = () => {
    switch (shipCountry) {
      case 'GB':
        return 'VAT & Import Duties';
      case 'CA':
        return 'Taxes (Import/Border)';
      default:
        return 'Tax';
    }
  };

  const getTotal = () => {
    let calculatedTotal = 0;

    choices.forEach((choice) => {
      const { type, price, quantity, salePrice } = choice;
      if (type === 'PROSPECT_SINGLE') {
        calculatedTotal += Number(price) * quantity;
      } else {
        calculatedTotal += Number(salePrice) * quantity;
      }
    });

    return (calculatedTotal + Number(taxes) + Number(shipping)).toFixed(2);
  };

  const getSavings = () => {
    let savings = 0;
    choices.forEach((choice) => {
      const { type, price, salePrice, quantity } = choice;
      if (type === 'INTRO' || type === 'REFILL') {
        savings += (Number(price) - Number(salePrice)) * quantity;
      }
    });
    return savings.toFixed(2);
  };

  const membershipLineItem = [
    [
      {
        description: (
          <div className={styles.productName}>
            <i className="fal fa-gift" />
            FabFitFun Membership
          </div>
        ),
      },
    ],
  ];

  const publicShopItems = choices.map((choice) => {
    const { type, name, quantity, salePrice, price } = choice;
    const displayQuantity = quantity > 1;
    const salePriceSubtotal = (Number(salePrice) * quantity).toFixed(2);
    const priceSubtotal = (Number(price) * quantity).toFixed(2);
    const quantityText = displayQuantity ? ` x${quantity}` : '';
    const displayedTitle =
      type === 'REFILL' ? `${name} Subscription${quantityText}` : `${name}${quantityText}`;
    return [
      {
        description: <div className={styles.productName}>{displayedTitle}</div>,
        price: formatCurrency(type === 'PROSPECT_SINGLE' ? priceSubtotal : salePriceSubtotal || 0),
        ...(type !== 'PROSPECT_SINGLE' && { crossedPrice: priceSubtotal }),
      },
    ];
  });

  const savingsLineItem = [
    [
      {
        description: (
          <div className={styles.savingsLineItem} data-testid="savings-line-item">
            <div className={styles.savingsLineItemText}>
              You&apos;re saving
              <span className={styles.savings}>{`$${getSavings()}`}</span>. That&apos;s nice!
            </div>
            <Tooltip
              content="This is your savings compared to vendor comp values. Prices vary by retailer and over time."
              placement="top"
              themes="left-aligned"
            >
              <div className={styles.savingsTooltip}>
                <Info />
              </div>
            </Tooltip>
          </div>
        ),
        rowClassName: 'savingsLineItemRow',
      },
    ],
  ];

  const displayedPublicShopItems = [...(hasIntro ? membershipLineItem : []), ...publicShopItems];

  const summaryItems = [
    ...displayedPublicShopItems,
    [
      {
        description: shipCountry === 'GB' ? 'P&P (Postage and Packaging)*' : 'Shipping*',
        price: formatCurrency(Number(shipping)),
      },
    ],
    [
      {
        description: <div className={styles.taxNote}>{TAX_NOTE}</div>,
      },
    ],
    [
      {
        description: getTaxText(),
        price:
          previewLoading || !shipZipCode ? (
            <div className={styles.taxesValue} data-testid="taxes-value">
              {LOADING_TAX}
            </div>
          ) : (
            <div data-testid="taxes-value">{formatCurrency(Number(taxes))}</div>
          ),
      },
    ],
    [
      {
        description: <b>Total</b>,
        price: <b data-testid="order-summary-total">{formatCurrency(getTotal())}</b>,
      },
    ],
    ...(hasIntro || hasRefill ? savingsLineItem : []),
  ];

  return (
    <div className={styles.orderSummaryContainer}>
      <OrderSummary
        summaryItems={summaryItems}
        shipCountry={shipCountry || 'US'}
        priceInGBP={priceInGBP}
        checkoutDisclaimer={
          <CheckOutDisclaimer
            subscribable={hasRefill}
            hasIntro={hasIntro}
            disclaimerPriceIncreaseFF={disclaimerPriceIncreaseFF}
            acceptedTerms={acceptedTerms}
            setAcceptedTerms={setAcceptedTerms}
          />
        }
        title="Order Summary"
      />
    </div>
  );
};

export default OrderSummaryContainer;
